"use client";

import * as React from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { PhoneCountryInput } from "@baluu/ui/components/phone-country-input";
import {
  validateEmail,
  validatePhone,
  validateGuest,
  hasGuestErrors,
  shouldEnforceBuyerPhone,
} from "./validation.ts";
import { InlineMarkdown } from "@baluu/ui/components/inline-markdown";

type PersonalDetailsValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  marketingOptIn: boolean;
  mandatoryTickBox?: boolean;
};

type PersonalDetailsErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

type PersonalDetailsFormProps = {
  values: PersonalDetailsValues;
  errors?: PersonalDetailsErrors;
  touched?: Partial<Record<keyof PersonalDetailsValues, boolean>>;
  onChange: (
    field: keyof PersonalDetailsValues,
    value: string | boolean,
  ) => void;
  onBlur?: (field: keyof PersonalDetailsValues) => void;
  disabled?: boolean;
  emailReadOnly?: boolean;
  emailReadOnlyHint?: string;
  showPhone?: boolean;
  /**
   * Whether the phone field is required. Defaults to true — every caller
   * predating the school-level phone setting asked for it unconditionally.
   * Only the label and the error display read this; the gate itself lives in
   * the caller's `validateGuest({ requirePhone })` call.
   */
  phoneRequired?: boolean;
  showMarketingOptIn?: boolean;
  marketingOptInLabel?: string;
  defaultCountryCode?: string;
  businessCountry?: string;
  /**
   * Which field groups to render. Omit to render all (default). Used by the
   * basket checkout's email-first flow to render the email field on its own,
   * then reveal the name/phone/marketing groups after a valid email.
   */
  visibleSections?: Array<"name" | "email" | "phone" | "marketing">;
  className?: string;
};

function PersonalDetailsForm({
  values,
  errors = {},
  touched = {},
  onChange,
  onBlur,
  disabled = false,
  emailReadOnly = false,
  emailReadOnlyHint,
  showPhone = true,
  phoneRequired = true,
  showMarketingOptIn = true,
  marketingOptInLabel = "I'd like to receive updates and offers via email",
  defaultCountryCode,
  businessCountry,
  visibleSections,
  className,
}: PersonalDetailsFormProps) {
  const show = (section: "name" | "email" | "phone" | "marketing") =>
    !visibleSections || visibleSections.includes(section);

  const handleChange =
    (field: keyof PersonalDetailsValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(
        field,
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
      );
    };

  const handleBlur = (field: keyof PersonalDetailsValues) => () => {
    onBlur?.(field);
  };

  const showError = (field: keyof PersonalDetailsErrors) =>
    touched[field] && errors[field];

  return (
    <div className={cn("space-y-4", className)}>
      {/* Name Row */}
      {show("name") && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First name *</Label>
          <Input
            id="firstName"
            type="text"
            value={values.firstName}
            onChange={handleChange("firstName")}
            onBlur={handleBlur("firstName")}
            disabled={disabled}
            placeholder="Enter your first name"
            className={cn(showError("firstName") && "border-destructive")}
          />
          {showError("firstName") && (
            <p className="text-sm text-destructive">{errors.firstName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last name *</Label>
          <Input
            id="lastName"
            type="text"
            value={values.lastName}
            onChange={handleChange("lastName")}
            onBlur={handleBlur("lastName")}
            disabled={disabled}
            placeholder="Enter your last name"
            className={cn(showError("lastName") && "border-destructive")}
          />
          {showError("lastName") && (
            <p className="text-sm text-destructive">{errors.lastName}</p>
          )}
        </div>
      </div>
      )}

      {/* Email with real-time validation */}
      {show("email") && (
      <div className="space-y-2">
        <Label htmlFor="email">Email address *</Label>
        <div className="relative">
          <Input
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange("email")}
            onBlur={handleBlur("email")}
            disabled={disabled || emailReadOnly}
            readOnly={emailReadOnly}
            placeholder="Enter your email address"
            className={cn(
              "pr-10",
              showError("email") && "border-destructive",
              touched.email &&
                values.email &&
                validateEmail(values.email).isValid &&
                "border-green-500",
              emailReadOnly && "bg-muted cursor-not-allowed",
            )}
          />
          {/* Validation icon */}
          {touched.email && values.email && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {validateEmail(values.email).isValid ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <AlertCircle className="w-4 h-4 text-destructive" />
              )}
            </div>
          )}
        </div>
        {showError("email") && (
          <p className="text-sm text-destructive">{errors.email}</p>
        )}
        {emailReadOnly && emailReadOnlyHint && (
          <p className="text-xs text-muted-foreground">{emailReadOnlyHint}</p>
        )}
        {/* Real-time validation hint */}
        {touched.email &&
          values.email &&
          !validateEmail(values.email).isValid &&
          !errors.email && (
            <p className="text-sm text-destructive">
              {validateEmail(values.email).error}
            </p>
          )}
      </div>
      )}

      {/* Phone with Country Code and real-time validation */}
      {show("phone") && showPhone && (
        <div className="space-y-2">
          <Label htmlFor="phone">Phone number{phoneRequired ? " *" : ""}</Label>
          <PhoneCountryInput
            value={values.phone}
            onChange={(fullPhone) => onChange("phone", fullPhone)}
            onBlur={handleBlur("phone")}
            defaultCountryCode={businessCountry || defaultCountryCode}
            disabled={disabled}
            hasError={!!(showError("phone") || (touched.phone && values.phone && !validatePhone(values.phone).isValid))}
            container={null}
          />
          {showError("phone") && (
            <p className="text-sm text-destructive">{errors.phone}</p>
          )}
          {touched.phone &&
            values.phone &&
            !validatePhone(values.phone).isValid &&
            !errors.phone && (
              <p className="text-sm text-destructive">
                {validatePhone(values.phone).error}
              </p>
            )}
        </div>
      )}

      {/* Marketing Opt-in */}
      {show("marketing") && showMarketingOptIn && (
        <div className="flex items-start gap-3 pt-2">
          <Checkbox
            id="marketingOptIn"
            checked={values.marketingOptIn}
            onCheckedChange={(checked) =>
              onChange("marketingOptIn", checked === true)
            }
            disabled={disabled}
          />
          <Label
            htmlFor="marketingOptIn"
            className="text-sm text-muted-foreground font-normal cursor-pointer"
          >
            <InlineMarkdown multiline>{marketingOptInLabel}</InlineMarkdown>
          </Label>
        </div>
      )}
    </div>
  );
}

// `validateGuest`/`hasGuestErrors`/`shouldEnforceBuyerPhone`/`validatePhone`
// are re-exported for callers that reach this component through the
// `@baluu/ui/components/checkout/*` subpath (which resolves .tsx only, so
// `./validation.ts` is unreachable that way) and need to gate on the same
// rules the checkout's personal step applies to these values — including the
// buyer-phone enforcement rule (asked+optional still format-checks once the
// buyer has typed something), not just the name/email gate.
export {
  PersonalDetailsForm,
  validateGuest,
  hasGuestErrors,
  shouldEnforceBuyerPhone,
  validatePhone,
};
export type { PersonalDetailsValues, PersonalDetailsErrors };
